/**
 * GZ Utils - Handles loading and decompression of gzipped GeoJSON files
 * Uses browser's built-in compression support for optimal performance
 */

import { LOGGING, APP_VERSION } from '/settings.js';

const CACHE_PREFIX = (window.location.hostname.replace(/\W+/g, '_') || 'mapapp') + '_gz_cache_';

/**
 * Gets a cached item from sessionStorage
 * @param {string} cacheKey - The cache key
 * @returns {Object|null} Cached data or null if not found/expired
 */
function getCachedItem(cacheKey) {
    try {
        const cachedItem = sessionStorage.getItem(cacheKey);
        if (cachedItem) {
            const { value, expiry } = JSON.parse(cachedItem);
            if (new Date().getTime() < expiry) {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[GZ Cache] HIT for: ${cacheKey}`);
                }
                return value;
            } else {
                sessionStorage.removeItem(cacheKey);
            }
        }
    } catch (e) {
        console.error("[GZ Cache] Read error:", e);
    }
    return null;
}

/**
 * Stores an item in sessionStorage cache
 * @param {string} cacheKey - The cache key
 * @param {Object} data - The data to cache
 * @param {number} durationInMinutes - Cache duration
 */
function setCachedItem(cacheKey, data, durationInMinutes = 120) {
    try {
        const expirationMs = durationInMinutes * 60 * 1000;
        const record = {
            value: data,
            expiry: new Date().getTime() + expirationMs,
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(record));
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[GZ Cache] Stored: ${cacheKey}`);
        }
    } catch (e) {
        console.error("[GZ Cache] Write error:", e);
    }
}

/**
 * Loads and decompresses a gzipped GeoJSON file with caching
 * @param {string} filePath - Path to the .gz file
 * @returns {Promise<Object>} Decompressed GeoJSON data
 */
export async function loadGzippedGeoJSON(filePath) {
    // Create cache key with version for cache-busting
    const versionedPath = filePath.includes('?')
        ? `${filePath}&v=${APP_VERSION}`
        : `${filePath}?v=${APP_VERSION}`;
    const cacheKey = `${CACHE_PREFIX}${versionedPath}`;

    // Check cache first
    const cachedData = getCachedItem(cacheKey);
    if (cachedData) {
        return cachedData;
    }

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log(`[GZ Utils] Loading gzipped file: ${filePath}`);
    }

    try {
        const response = await fetch(filePath);

        if (!response.ok) {
            if (response.status === 404) {
                // File doesn't exist - return empty FeatureCollection
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log(`[GZ Utils] File not found: ${filePath}, returning empty layer`);
                }
                const emptyData = {
                    type: 'FeatureCollection',
                    features: []
                };
                setCachedItem(cacheKey, emptyData, 60); // Cache empty data for 1 hour
                return emptyData;
            } else {
                throw new Error(`HTTP error! status: ${response.status} for file: ${filePath}`);
            }
        }

        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log(`[GZ Utils] Response headers for ${filePath}:`, {
                'content-type': response.headers.get('content-type'),
                'content-encoding': response.headers.get('content-encoding'),
                'content-length': response.headers.get('content-length')
            });
        }

        // PHP development server doesn't automatically decompress gzipped files
        // So we always need to handle decompression manually
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log(`[GZ Utils] Manual decompression required for ${filePath}`);
        }

        const decompressedData = await decompressGzipManually(response);

        // Cache the decompressed data
        setCachedItem(cacheKey, decompressedData, 120); // Cache for 2 hours

        return decompressedData;

    } catch (error) {
        console.error(`[GZ Utils] Error loading gzipped file ${filePath}:`, error);

        // If it's a network error (file doesn't exist), return empty FeatureCollection
        if (error.message.includes('Failed to fetch') || error.message.includes('404')) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log(`[GZ Utils] File not accessible: ${filePath}, returning empty layer`);
            }
            const emptyData = {
                type: 'FeatureCollection',
                features: []
            };
            setCachedItem(cacheKey, emptyData, 60); // Cache empty data for 1 hour
            return emptyData;
        }

        throw error;
    }
}

/**
 * Manually decompresses gzip data using browser APIs
 * @param {Response} response - Fetch response with gzipped data
 * @returns {Promise<Object>} Decompressed JSON data
 */
async function decompressGzipManually(response) {
    try {
        // Get the compressed data as an ArrayBuffer
        const compressedData = await response.arrayBuffer();

        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Got compressed data, size:', compressedData.byteLength);
        }

        // Use the Compression Streams API if available (modern browsers)
        if (window.CompressionStream) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log('[GZ Utils] Using Compression Streams API');
            }
            return await decompressWithCompressionAPI(compressedData);
        } else {
            // Fallback: try to use pako or other library
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log('[GZ Utils] Compression Streams API not available, trying alternative');
            }

            // For now, let's try to load the file as regular JSON (in case it's not actually gzipped)
            try {
                const text = await response.text();
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log('[GZ Utils] Trying to parse as regular JSON');
                }
                return JSON.parse(text);
            } catch (jsonError) {
                throw new Error('Gzip decompression not supported in this browser and file is not valid JSON. Please use a modern browser or ensure server provides decompressed data.');
            }
        }
    } catch (error) {
        console.error('[GZ Utils] Manual decompression failed:', error);
        throw error;
    }
}

/**
 * Decompresses using the modern Compression Streams API
 * @param {ArrayBuffer} compressedData - Gzipped data
 * @returns {Promise<Object>} Decompressed JSON data
 */
async function decompressWithCompressionAPI(compressedData) {
    try {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Starting Compression Streams API decompression');
        }

        const decompressionStream = new DecompressionStream('gzip');
        const decompressedStream = new Response(compressedData).body.pipeThrough(decompressionStream);
        const decompressedResponse = new Response(decompressedStream);

        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Decompression stream created, parsing JSON...');
        }

        const result = await decompressedResponse.json();

        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Successfully decompressed and parsed JSON:', {
                type: result.type,
                featuresCount: result.features?.length || 0
            });
        }

        return result;
    } catch (error) {
        console.error('[GZ Utils] Compression Streams API failed:', error);
        throw error;
    }
}

/**
 * Loads multiple gzipped GeoJSON layers and combines them
 * @param {Object} layerPaths - Object with layer paths { background, borders, roads }
 * @param {Object} layerSettings - Object with layer visibility settings
 * @returns {Promise<Object>} Combined GeoJSON FeatureCollection
 */
export async function loadMapLayers(layerPaths, layerSettings) {
    const layers = [];
    const promises = [];

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log('[GZ Utils] Starting to load map layers...');
        console.log('[GZ Utils] Layer settings:', layerSettings);
        console.log('[GZ Utils] Layer paths:', layerPaths);
    }

    // Load each enabled layer in the desired stacking order
    // Background first (bottom), then roads (middle), then borders (top)

    if (layerSettings.BACKGROUND && layerPaths.BACKGROUND) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Loading background layer from:', layerPaths.BACKGROUND);
        }
        promises.push(loadGzippedGeoJSON(layerPaths.BACKGROUND).then(data => {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log(`[GZ Utils] Loaded background layer with ${data.features?.length || 0} features`);
            }
            return { type: 'background', data };
        }).catch(error => {
            console.error('[GZ Utils] Failed to load background layer:', error);
            throw error;
        }));
    }

    if (layerSettings.ROADS && layerPaths.ROADS) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Loading roads layer from:', layerPaths.ROADS);
        }
        promises.push(loadGzippedGeoJSON(layerPaths.ROADS).then(data => {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log(`[GZ Utils] Loaded roads layer with ${data.features?.length || 0} features`);
            }
            return { type: 'roads', data };
        }).catch(error => {
            console.error('[GZ Utils] Failed to load roads layer:', error);
            throw error;
        }));
    }

    if (layerSettings.BORDERS && layerPaths.BORDERS) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[GZ Utils] Loading borders layer from:', layerPaths.BORDERS);
        }
        promises.push(loadGzippedGeoJSON(layerPaths.BORDERS).then(data => {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log(`[GZ Utils] Loaded borders layer with ${data.features?.length || 0} features`);
            }
            return { type: 'borders', data };
        }).catch(error => {
            console.error('[GZ Utils] Failed to load borders layer:', error);
            throw error;
        }));
    }

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log(`[GZ Utils] Attempting to load ${promises.length} layers...`);
    }

    // Wait for all layers to load
    const loadedLayers = await Promise.all(promises);

    // Combine all features into a single FeatureCollection, preserving layer information
    const allFeatures = [];
    loadedLayers.forEach((layerInfo, index) => {
        if (layerInfo.data && layerInfo.data.features) {
            // Add layer type to each feature's properties
            layerInfo.data.features.forEach(feature => {
                if (!feature.properties) {
                    feature.properties = {};
                }
                feature.properties._layerType = layerInfo.type;
                allFeatures.push(feature);
            });

            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log(`[GZ Utils] Layer ${index} (${layerInfo.type}) contributed ${layerInfo.data.features.length} features`);
            }
        } else {
            console.warn(`[GZ Utils] Layer ${index} has no features:`, layerInfo);
        }
    });

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log(`[GZ Utils] Combined ${allFeatures.length} features from ${loadedLayers.length} layers`);
    }

    const result = {
        type: 'FeatureCollection',
        features: allFeatures
    };

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log('[GZ Utils] Final combined map data:', result);
    }

    return result;
}

/**
 * Validates that a GeoJSON object has the correct structure
 * @param {Object} geoJSON - The GeoJSON object to validate
 * @returns {boolean} True if valid
 */
export function validateGeoJSON(geoJSON) {
    return geoJSON &&
        geoJSON.type === 'FeatureCollection' &&
        Array.isArray(geoJSON.features);
}

/**
 * Clears all cached gzipped files
 */
export function clearGzCache() {
    Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
            sessionStorage.removeItem(key);
        }
    });
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[GZ Cache] All gzipped file cache cleared');
    }
}

/**
 * Clears cache for a specific gzipped file
 * @param {string} filePath - Path to the .gz file
 */
export function clearGzCacheForFile(filePath) {
    const versionedPath = filePath.includes('?')
        ? `${filePath}&v=${APP_VERSION}`
        : `${filePath}?v=${APP_VERSION}`;
    const cacheKey = `${CACHE_PREFIX}${versionedPath}`;
    sessionStorage.removeItem(cacheKey);
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log(`[GZ Cache] Cleared cache for: ${filePath}`);
    }
} 